import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // CarSavr brand variants (iter-409) — canonical button styles.
        // Sharp corners (rounded-none) per the site's Swiss aesthetic.
        primary:
          "rounded-none bg-[#0A2540] hover:bg-slate-900 text-white font-bold transition-all hover:-translate-y-0.5",
        accent:
          "rounded-none bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold transition-all hover:-translate-y-0.5",
        outlineNavy:
          "rounded-none border border-[#0A2540] hover:bg-[#0A2540] hover:text-white text-[#0A2540] font-bold transition-all",
        dark:
          "rounded-none bg-slate-950 hover:bg-slate-800 text-white font-bold transition-all",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // CarSavr brand sizes — match the site's existing CTA scale.
        brand: "h-11 px-5",
        brandLg: "h-12 px-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
